import { FormEvent, useState } from 'react'
import { Download, FileSpreadsheet } from 'lucide-react'
import { Alerta, Boton, Campo, Dialogo, Entrada } from '../kit'
import { useDescargarPlantilla } from '../../queries/useInventario'
import { CantidadesNuevos } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'

const MAX_POR_TIPO = 1000
const TIPOS: { clave: keyof CantidadesNuevos; etiqueta: string }[] = [
  { clave: 'inventariables', etiqueta: 'Inventariables' },
  { clave: 'no_inventariables', etiqueta: 'No inventariables' },
  { clave: 'otros', etiqueta: 'Otros' },
]
const valido = (texto: string) => /^\d+$/.test(texto.trim()) && Number(texto) <= MAX_POR_TIPO

/**
 * Agregar bienes en masa: se dice cuántos de cada tipo y se descarga una planilla con
 * justo esas filas, cada una con su Tipo. Se completa en Excel (la oficina se escribe
 * como se la conoce: 327, BP-P-2-04) y se sube en «Cargar planilla». Todos quedan con la
 * etiqueta pendiente y sin evaluar.
 */
export function DialogoAgregarBienes({ onCerrar, enlaceCargar = true }: { onCerrar: () => void; enlaceCargar?: boolean }) {
  const descargar = useDescargarPlantilla()
  const [texto, setTexto] = useState<Record<keyof CantidadesNuevos, string>>({ inventariables: '0', no_inventariables: '0', otros: '0' })
  const todosValidos = TIPOS.every((t) => valido(texto[t.clave]))
  const total = todosValidos ? TIPOS.reduce((suma, t) => suma + Number(texto[t.clave]), 0) : 0

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    descargar.mutate({
      inventariables: Number(texto.inventariables),
      no_inventariables: Number(texto.no_inventariables),
      otros: Number(texto.otros),
    })
  }

  return (
    <Dialogo abierto onCerrar={onCerrar} titulo="Agregar bienes">
      <form className="pila" onSubmit={enviar}>
        <p>
          ¿Cuántos bienes vas a agregar? La planilla trae una fila para cada uno con su tipo puesto. Todos quedan con la etiqueta
          pendiente.
        </p>
        <div className="formulario-grilla formulario-grilla--angosta">
          {TIPOS.map((t) => (
            <Campo key={t.clave} etiqueta={t.etiqueta} error={valido(texto[t.clave]) ? null : `De 0 a ${MAX_POR_TIPO}.`}>
              <Entrada
                type="number"
                inputMode="numeric"
                min={0}
                max={MAX_POR_TIPO}
                step={1}
                value={texto[t.clave]}
                onChange={(e) => setTexto({ ...texto, [t.clave]: e.target.value })}
                onFocus={(e) => e.target.select()}
              />
            </Campo>
          ))}
        </div>
        <p className="texto-secundario" aria-live="polite">
          {total === 0 ? 'Indica al menos un bien.' : `La planilla tendrá ${total.toLocaleString('es-CL')} ${total === 1 ? 'fila' : 'filas'}.`}
        </p>
        {descargar.isError && <Alerta tipo="error">{mensajeDeError(descargar.error)}</Alerta>}
        {descargar.isSuccess && (
          <Alerta tipo="success" titulo="Planilla descargada">
            Complétala en Excel: descripción y oficina (como la conoces, por ejemplo 327 o BP-P-2-04). Después súbela en «Cargar
            planilla».
          </Alerta>
        )}
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cerrar</Boton>
          {descargar.isSuccess && enlaceCargar && (
            <Boton variante="outlined" icono={FileSpreadsheet} to="/app/bienes/planilla">Cargar planilla</Boton>
          )}
          <Boton type="submit" icono={Download} cargando={descargar.isPending} disabled={total === 0}>
            Descargar planilla
          </Boton>
        </div>
      </form>
    </Dialogo>
  )
}
